import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eventsAPI } from '@/services';
import { useToast } from '../components/Toast';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import BackToCalendarButton from '../components/details/BackToCalendarButton';
import InfoTile from '../components/details/InfoTile';
import {
  MemberMobileShell,
  MemberMobileHero,
} from '../components/MemberMobileShell';
import { isMemberLikeEntreaUser } from '../utils/iamRoles';

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { error, success } = useToast();
  const { hasPermission, role } = usePermissions();
  const { user: currentUser, isAuthenticated } = useAuth();
  const hasMemberProfileLink = !!(currentUser?.member_id || currentUser?.person_id);
  const isMembre = isMemberLikeEntreaUser(role, hasMemberProfileLink);
  const { settings } = useSettings();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [userRegistration, setUserRegistration] = useState(null);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  
  const paymentMethods = settings?.financial?.payment_methods || ['banque', 'mobile_money', 'espèces', 'virement', 'chèque'];
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  const [registrationData, setRegistrationData] = useState({
    name: '',
    first_name: '',
    whatsapp: '',
    payment_method: '',
    payment_proof_url: ''
  });

  useEffect(() => {
    loadEventDetails();
  }, [id]);

  const loadEventDetails = async () => {
    try {
      setLoading(true);
      const eventResponse = await eventsAPI.getById(id);
      const eventData = eventResponse.data;
      setEvent(eventData);
      
      // Si l'API retourne user_registration, l'utiliser directement
      if (eventData.user_registration) {
        setUserRegistration(eventData.user_registration);
      } else if (eventData.is_registered && isAuthenticated && currentUser?.member_id) {
        // Sinon, si l'utilisateur est inscrit, essayer de récupérer les détails (si admin)
        try {
          if (hasPermission('events.view')) {
            const registrationsResponse = await eventsAPI.getRegistrations(id);
            const registrations = registrationsResponse.data.registrations || registrationsResponse.data || [];
            const userReg = registrations.find(r => r.member_id === currentUser.member_id);
            if (userReg) {
              setUserRegistration(userReg);
            }
          }
        } catch (err) {
          // Si on n'a pas la permission, on laisse userRegistration à null
          console.log('Cannot load registration details (permission denied)');
        }
      }
    } catch (err) {
      console.error('Failed to load event details:', err);
      error(err.response?.data?.detail || err.message || 'Erreur lors du chargement de l\'événement');
      navigate('/calendar');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!event.registration_open) {
      error('Les inscriptions sont fermées pour cet événement');
      return;
    }

    if (!event.is_public && (!isAuthenticated || !currentUser?.member_id)) {
      error('Cet événement est réservé aux membres. Veuillez vous connecter.');
      return;
    }

    setRegistering(true);
    try {
      const dataToSend = {
        ...registrationData,
        payment_method: !event.is_free ? registrationData.payment_method : undefined,
        payment_proof_url: !event.is_free ? registrationData.payment_proof_url : undefined
      };

      const response = await eventsAPI.register(id, dataToSend);
      setUserRegistration(response.data);
      setShowRegistrationForm(false);
      success(event.is_free ? 'Inscription réussie ! Votre code de confirmation est affiché ci-dessous.' : 'Inscription soumise avec succès ! Vous recevrez une confirmation après vérification du paiement.');
      loadEventDetails();
    } catch (err) {
      console.error('Registration error:', err);
      let errorMessage = 'Erreur lors de l\'inscription';
      
      if (err.response?.data) {
        const errorData = err.response.data;
        // Gérer les erreurs de validation Pydantic (array d'objets)
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.detail?.msg) {
          errorMessage = errorData.detail.msg;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      error(errorMessage);
    } finally {
      setRegistering(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center bg-gradient-to-b from-[#f2f6fb] to-[#eef3f8] py-16">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-[#113a5b]" />
          <p className="mt-4 text-sm text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center bg-gradient-to-b from-[#f2f6fb] to-[#eef3f8] px-4 py-16">
        <div className="text-center">
          <p className="text-slate-700">Événement introuvable</p>
          <button
            type="button"
            onClick={() => navigate('/calendar')}
            className="mt-4 min-h-[44px] rounded-lg bg-[#113a5b] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0f3350] md:text-base"
          >
            Retour au calendrier
          </button>
        </div>
      </div>
    );
  }

  const isEventPast = new Date(event.start_date) < new Date();
  const isConfirmed = userRegistration?.registration_status === 'confirmed';
  const canRegister = !isEventPast && event.registration_open && !isConfirmed && (!event.is_public || isAuthenticated || !event.is_public);
  const showRegistrationButton = canRegister && !userRegistration && (!event.is_public ? (isAuthenticated && currentUser?.member_id) : true);

  const eventIntroDescription = (() => {
    try {
      const start = event.start_date
        ? new Date(event.start_date).toLocaleString('fr-FR', {
            dateStyle: 'medium',
            timeStyle: 'short',
          })
        : '';
      const parts = [start, event.location].filter(Boolean);
      return parts.join(' · ') || 'Détails de l’événement';
    } catch {
      return 'Détails de l’événement';
    }
  })();

  return (
    <div className="min-h-[60vh] bg-gradient-to-b from-[#f2f6fb] to-[#eef3f8] md:bg-gradient-to-br md:from-gray-50 md:via-white md:to-entrea-panel/35">
      <div className="mx-auto max-w-6xl px-3 py-4 md:px-4 md:py-8">
        {/* Header avec bouton retour */}
        <div className="mb-4 md:mb-6">
          <BackToCalendarButton onClick={() => navigate('/calendar')} />
        </div>

        {isMembre && (
          <MemberMobileShell>
            <MemberMobileHero
              label="Événement"
              title={event.title}
              hint={eventIntroDescription}
              tone={event.registration_open === false ? 'warn' : 'info'}
              actions={
                <>
                  {event.meeting_link ? (
                    <a
                      href={event.meeting_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mm-cta flex-1"
                    >
                      Rejoindre
                    </a>
                  ) : null}
                  {event.location ? (
                    <span className="mm-cta mm-cta--ghost flex-1 text-center text-xs">
                      {event.location}
                    </span>
                  ) : null}
                </>
              }
            />
          </MemberMobileShell>
        )}

        {/* Affiche et Détails de l'événement */}
        <div className="mb-8 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl">
          <div className="flex flex-col lg:flex-row">
            {/* Affiche à gauche */}
            {event.poster_url && (
              <div className="lg:w-2/5 flex-shrink-0 overflow-hidden bg-gradient-to-br from-gray-100 to-gray-50">
                <img 
                  src={BACKEND_URL ? `${BACKEND_URL}${event.poster_url}` : event.poster_url}
                  alt={event.title}
                  className="h-full min-h-[11rem] w-full object-cover md:min-h-[300px] lg:min-h-[600px]"
                />
              </div>
            )}
            
            {/* Détails à droite */}
            <div className={`flex-1 p-4 md:p-8 lg:p-10 ${event.poster_url ? 'lg:border-l border-gray-200' : ''}`}>
          {/* En-tête avec titre et badges */}
          <div className={`mb-8 ${isMembre ? 'hidden md:block' : ''}`}>
            <div className="flex flex-wrap items-start gap-3 mb-6">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 flex-1 leading-tight">{event.title}</h1>
              <div className="flex flex-wrap gap-2">
                {!event.is_public && (
                  <span className="px-3 py-1.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-800 border border-gray-200">
                    Privé
                  </span>
                )}
                {!event.is_free && event.price && (
                  <span className="px-3 py-1.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
                    {event.price} BIF
                  </span>
                )}
                {event.is_free && (
                  <span className="px-3 py-1.5 text-xs font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
                    Gratuit
                  </span>
                )}
                {!event.registration_open && (
                  <span className="px-3 py-1.5 text-xs font-semibold rounded-full bg-red-100 text-red-800 border border-red-200">
                    Inscriptions fermées
                  </span>
                )}
              </div>
            </div>

            {/* Informations principales */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm md:text-base">
              <InfoTile
                tone="indigo"
                title="Date de début"
                icon={(
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                )}
              >
                {formatDate(event.start_date)}
              </InfoTile>

              {event.end_date && (
                <InfoTile
                  tone="purple"
                  title="Date de fin"
                  icon={(
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                  )}
                >
                  {formatDate(event.end_date)}
                </InfoTile>
              )}

              {event.location && (
                <InfoTile
                  tone="blue"
                  title="Lieu"
                  icon={(
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                  )}
                >
                  {event.location}
                </InfoTile>
              )}

              {event.type === 'meeting' && event.meeting_mode && (
                <InfoTile
                  tone="green"
                  title="Mode de réunion"
                  icon={(
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                    </svg>
                  )}
                >
                  {event.meeting_mode}
                </InfoTile>
              )}

              {event.meeting_link && (
                <InfoTile
                  tone="pink"
                  title="Lien de réunion"
                  icon={(
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                    </svg>
                  )}
                >
                  <a href={event.meeting_link} target="_blank" rel="noopener noreferrer" className="text-entrea-royal hover:text-entrea-navy font-medium inline-flex items-center gap-1">
                    Rejoindre la réunion
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </InfoTile>
              )}
            </div>
          </div>

              {/* Description */}
              {event.description && (
                <div className="border-t border-gray-200 pt-8 mt-8">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Description</h2>
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed text-base">{event.description}</p>
                </div>
              )}

              {/* Lien public pour partage (événements publics uniquement) - Pour les admins */}
              {event.is_public && hasPermission('events.view') && (
                <div className="border-t border-gray-200 pt-6">
                  <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-3">Lien public partageable</h2>
                  <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                    <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 break-all">
                      <code className="text-sm text-gray-700">
                        {window.location.origin}/events/public/{event.id}
                      </code>
                    </div>
                    <button
                      onClick={async () => {
                        const url = `${window.location.origin}/events/public/${event.id}`;
                        try {
                          await navigator.clipboard.writeText(url);
                          success('Lien copié dans le presse-papiers !');
                        } catch (err) {
                          // Fallback pour les navigateurs qui ne supportent pas clipboard API
                          const textArea = document.createElement('textarea');
                          textArea.value = url;
                          document.body.appendChild(textArea);
                          textArea.select();
                          document.execCommand('copy');
                          document.body.removeChild(textArea);
                          success('Lien copié dans le presse-papiers !');
                        }
                      }}
                      className="flex items-center gap-2 whitespace-nowrap rounded-lg bg-entrea-royal px-4 py-2.5 text-sm font-medium text-white transition hover:bg-entrea-navy"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copier le lien
                    </button>
                  </div>
                  <p className="mt-2 text-sm text-gray-500">Partagez ce lien pour permettre au public de s'inscrire à cet événement.</p>
                </div>
              )}

              {/* Organisateur */}
              {event.organizer && (
                <div className="border-t border-gray-200 pt-8 mt-8">
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Organisateur</h2>
                  <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-entrea-panel to-entrea-surface rounded-xl border border-entrea-panel">
                    <div className="w-14 h-14 bg-gradient-to-br from-entrea-ink to-entrea-royal rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                      <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-lg">{event.organizer.name}</p>
                      {event.organizer.email && (
                        <p className="text-sm text-gray-600 mt-1">{event.organizer.email}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Section Inscription / Confirmation */}
        {!isEventPast && (
          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 lg:p-10 border border-gray-200">
            {userRegistration ? (
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Votre inscription</h2>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
                      userRegistration.registration_status === 'approved' || userRegistration.registration_status === 'confirmed'
                        ? 'bg-green-100 text-green-800'
                        : userRegistration.registration_status === 'rejected'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {userRegistration.registration_status === 'approved' ? 'Approuvé' :
                       userRegistration.registration_status === 'confirmed' ? 'Confirmé' :
                       userRegistration.registration_status === 'rejected' ? 'Rejeté' :
                       'En attente'}
                    </span>
                  </div>

                  {userRegistration.confirmation_code && (
                    <div className="bg-entrea-panel border border-entrea-sky/40 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-entrea-navy mb-2">Code de confirmation</h3>
                      <p className="text-3xl font-bold text-entrea-royal mb-4 text-center tracking-wider">
                        {userRegistration.confirmation_code}
                      </p>
                      <p className="text-sm text-entrea-ink text-center">
                        Présentez ce code le jour de l'événement pour confirmer votre présence.
                      </p>
                    </div>
                  )}

                  {userRegistration.qr_code_url && (
                    <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                      <p className="text-sm font-medium text-gray-700 mb-4">QR Code de confirmation</p>
                      <div className="inline-block p-4 bg-white border-2 border-gray-300 rounded-lg">
                        <img 
                          src={`${BACKEND_URL}${userRegistration.qr_code_url}`}
                          alt="QR Code de confirmation"
                          className="w-48 h-48 mx-auto"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-4">
                        Scannez ce QR Code à l'entrée de l'événement
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : showRegistrationButton ? (
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">S'inscrire à cet événement</h2>
                {!showRegistrationForm ? (
                  <button
                    onClick={() => setShowRegistrationForm(true)}
                    className="px-6 py-3 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition font-medium"
                  >
                    S'inscrire
                  </button>
                ) : (
                  <form onSubmit={handleRegister} className="space-y-4">
                    {!isAuthenticated && event.is_public && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                          <input
                            type="text"
                            value={registrationData.name}
                            onChange={(e) => setRegistrationData({ ...registrationData, name: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                            required={!isAuthenticated && event.is_public}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                          <input
                            type="text"
                            value={registrationData.first_name}
                            onChange={(e) => setRegistrationData({ ...registrationData, first_name: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                            required={!isAuthenticated && event.is_public}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Numéro WhatsApp *</label>
                          <input
                            type="tel"
                            value={registrationData.whatsapp}
                            onChange={(e) => setRegistrationData({ ...registrationData, whatsapp: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                            required={!isAuthenticated && event.is_public}
                            placeholder="+257 XX XX XX XX"
                          />
                        </div>
                      </>
                    )}

                    {!event.is_free && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Méthode de paiement *</label>
                          <select
                            value={registrationData.payment_method}
                            onChange={(e) => setRegistrationData({ ...registrationData, payment_method: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                            required={!event.is_free}
                          >
                            <option value="">Sélectionnez une méthode</option>
                            {paymentMethods.map(method => (
                              <option key={method} value={method}>{method}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">URL de preuve de paiement (optionnel)</label>
                          <input
                            type="url"
                            value={registrationData.payment_proof_url || ''}
                            onChange={(e) => setRegistrationData({ ...registrationData, payment_proof_url: e.target.value })}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-entrea-sky"
                            placeholder="https://..."
                          />
                        </div>
                      </>
                    )}

                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowRegistrationForm(false);
                          setRegistrationData({
                            name: '',
                            first_name: '',
                            whatsapp: '',
                            payment_method: '',
                            payment_proof_url: ''
                          });
                        }}
                        className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                      >
                        Annuler
                      </button>
                      <button
                        type="submit"
                        disabled={registering}
                        className="px-6 py-2.5 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {registering ? 'Inscription en cours...' : 'Confirmer l\'inscription'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ) : !event.is_public && !isAuthenticated ? (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">Cet événement est réservé aux membres.</p>
                <button
                  onClick={() => navigate('/login')}
                  className="rounded-lg bg-entrea-royal px-6 py-2.5 font-medium text-white transition hover:bg-entrea-navy"
                >
                  Se connecter
                </button>
              </div>
            ) : !event.registration_open ? (
              <div className="text-center py-8">
                <p className="text-gray-600">Les inscriptions sont fermées pour cet événement.</p>
              </div>
            ) : null}
          </div>
        )}

        {/* Actions administratives */}
        {hasPermission('events.view') && (
          <div className="mt-8 bg-white rounded-2xl shadow-xl border border-gray-200 p-6 md:p-8">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">Actions administratives</h2>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => navigate(`/events/${id}/registrations`)}
                className="flex transform items-center gap-2 rounded-xl bg-gradient-to-r from-entrea-royal to-entrea-navy px-6 py-3 font-medium text-white shadow-lg transition hover:from-entrea-navy hover:to-entrea-navy hover:shadow-xl hover:scale-105"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                Voir les inscriptions
              </button>
              <button
                onClick={() => navigate(`/calendar?edit=${id}`)}
                className="px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 transition font-medium flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Modifier l'événement
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDetails;

